import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { useTailwindBreakpoint } from '@swyftx/aviary/hooks/useTailwindBreakpoint';
import { useTailwindContainerBreakpoint } from '@swyftx/aviary/hooks/useTailwindContainerBreakpoint';
import { EnhancedTableData, EnhancedTableHeaderData, EnhancedTableSort } from '@swyftx/aviary/molecules/EnhancedTable';
import { MarginPosition, MarginPositionStatus } from '@swyftx/margin/build/types';

import { MarginPositionsTableActions } from '@routes/SwyftxMargin/components/Trade/MarginPositionsCard/Tables/MarginPositionsTable/components';

import { useMarginPositions } from 'src/lib/margin/hooks/positions';

import { MarginPositionsHealthTableData } from './MarginPositionsHealthTable.types';
import {
  MarginProfitLoss,
  MarginProfitLossChart,
  MarginSide,
  MarginUsedMargin,
  PositionInstrument,
  PositionInstrumentSymbol,
} from '../../../General';

type MarginPositionsHeaderData = { [key in keyof MarginPositionsHealthTableData]: EnhancedTableHeaderData };

type Props = {
  pageSize?: number;
};

const useMarginPositionsHealthTable = ({ pageSize = 25 }: Props) => {
  const [sort, setSort] = useState<EnhancedTableSort<MarginPositionsHealthTableData>>({
    sortKey: 'profitLoss',
    sortDirection: 'DESC',
  });

  const { positions, isLoading, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useMarginPositions({
    pageSize,
    status: [MarginPositionStatus.Open],
    sortBy: 'lastUserAction',
    sortDir: 'desc',
  });
  const { t } = useTranslation('margin');
  const isXs = useTailwindBreakpoint('xs');
  const { atLeast } = useTailwindContainerBreakpoint({ containerName: 'position-health-table' });

  const headers: MarginPositionsHeaderData = useMemo(
    () => ({
      instrument: {
        title: t('tables.positions.headers.instrument'),
        alignment: 'start',
        sortable: false,
        enabled: atLeast.md,
        className: 'max-w-[176px] pl-0',
      },
      direction: {
        title: t('tables.positions.headers.direction'),
        alignment: 'start',
        sortable: false,
        enabled: atLeast.md,
      },

      instrumentDirection: {
        title: t('tables.positions.headers.instrument'),
        alignment: 'start',
        className: 'pl-0',
        sortable: false,
        enabled: !atLeast.md,
      },

      usedMargin: {
        title: t('tables.positions.headers.usedMargin'),
        alignment: 'end',
        sortable: false,
        enabled: !isXs,
      },

      profitLoss: {
        title: t('tables.positions.headers.profitLoss'),
        alignment: 'end',
        sortable: false,
        enabled: !isXs,
      },

      profitLossChart: {
        title: '',
        alignment: 'start',
        sortable: false,
        enabled: atLeast.sm,
        className: 'max-w-[140px]',
      },

      actions: {
        title: t('tables.positions.headers.actions'),
        className: 'pr-0',
        alignment: 'end',
        sortable: false,
        enabled: !isXs,
      },

      profitLossMobileChart: {
        title: t('tables.positions.headers.profitLoss'),
        className: 'pr-0',
        alignment: 'end',
        sortable: false,
        enabled: isXs,
      },
    }),
    [atLeast.md, atLeast.sm, isXs, t],
  );

  const buildDataItem = useCallback(
    (position: MarginPosition) => ({
      value: position,
      instrument: {
        value: position.marketUuid,
        element: <PositionInstrument position={position} size='medium' weight='bold' />,
      },
      direction: {
        value: position.sell,
        element: <MarginSide positionId={position.positionId} dynamicColors={false} weight='bold' />,
      },

      instrumentDirection: {
        value: position.marketUuid,
        element: (
          <FlexLayout alignItems='center' spacing={8}>
            <PositionInstrument position={position} size='small' weight='bold' showMarketSymbol={false} />
            <FlexLayout direction='column' spacing={2}>
              <PositionInstrumentSymbol position={position} color='primary' weight='bold' />
              <MarginSide positionId={position.positionId} dynamicColors={false} color='secondary' size='small' />
            </FlexLayout>
          </FlexLayout>
        ),
      },

      usedMargin: {
        value: position.marginUsed,
        element: <MarginUsedMargin positionId={position.positionId} weight='bold' size='medium' justifyContent='end' />,
      },

      profitLoss: {
        value: position.lastUpdate,
        element: <MarginProfitLoss positionId={position.positionId} weight='bold' justifyContent='end' size='medium' />,
      },

      profitLossChart: {
        value: position.lastUpdate,
        element: <MarginProfitLossChart positionId={position.positionId} />,
      },

      profitLossMobileChart: {
        value: '',
        element: (
          <FlexLayout alignItems='end' direction='column' spacing={8}>
            <MarginProfitLoss positionId={position.positionId} weight='emphasis' justifyContent='end' />
            <MarginProfitLossChart positionId={position.positionId} className='w-full sm:w-[140px]' />
          </FlexLayout>
        ),
      },

      actions: {
        value: '',
        element: <MarginPositionsTableActions positionId={position.positionId} />,
      },
    }),
    [],
  );

  const filteredPositions = useMemo(() => {
    if (!positions) return [];

    return positions.filter((p) => p.status === MarginPositionStatus.Open);
  }, [positions]);

  const data: EnhancedTableData<MarginPositionsHealthTableData, MarginPosition>[] = useMemo(
    () => filteredPositions.map((position: MarginPosition) => buildDataItem(position)),
    [buildDataItem, filteredPositions],
  );

  const onSort = useCallback(
    (newSort?: EnhancedTableSort<MarginPositionsHealthTableData>) => {
      const tableData: EnhancedTableData<MarginPositionsHealthTableData, MarginPosition>[] = Object.assign([], data);

      if (!newSort) return tableData;

      setSort(newSort);

      return tableData;
    },
    [data],
  );

  return {
    initialSort: sort,
    headers,
    data,
    isLoading,
    onSort,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
  };
};

export { useMarginPositionsHealthTable };
